import { useMemo, useState } from 'react';
import { differenceInCalendarDays, format } from 'date-fns';
import type { EpcScheduleRevisionSummary } from '../../../../services/epcProjectService';
import { PmoCard, PmoCardHeader } from './ui/PmoCard';
import { buildRevisionLanes, pctOnAxis, type MilestoneVM } from './revisionLanes';

const DELTA_CHIP: Record<'bad' | 'good' | 'zero', string> = {
  bad: 'border-[#fecdd3] bg-[#fff1f2] text-[#be123c]',
  good: 'border-[#a7f3d0] bg-[#ecfdf5] text-[#047857]',
  zero: 'border-[#e2e8f0] bg-[#f1f5f9] text-[#475569]',
};

function fmtDay(d: Date | string | null | undefined): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  return Number.isNaN(date.getTime()) ? '—' : format(date, 'dd MMM yyyy');
}

export interface RevisionTimelineCardProps {
  revisions: EpcScheduleRevisionSummary[];
  revisionsUnavailable: boolean;
  project: { startDate?: string; endDatePlanned?: string; commissioningDate?: string };
  milestones: MilestoneVM[];
  plannedTodayPct: number;
  actualPct: number | null;
  now?: Date;
}

export function RevisionTimelineCard({
  revisions,
  revisionsUnavailable,
  project,
  milestones,
  plannedTodayPct,
  actualPct,
  now = new Date(),
}: RevisionTimelineCardProps) {
  const [expanded, setExpanded] = useState(false);
  const fallbackMode = revisionsUnavailable || revisions.length === 0;

  const effective: EpcScheduleRevisionSummary[] = useMemo(() => {
    if (!fallbackMode) return revisions;
    if (!project.startDate || !(project.endDatePlanned || project.commissioningDate)) return [];
    return [
      {
        version: 1,
        importedAt: project.startDate,
        createdByName: '',
        taskCount: 0,
        planStart: project.startDate,
        planFinish: project.endDatePlanned ?? project.commissioningDate ?? null,
        deltaFinishDays: 0,
        deltaTaskCount: 0,
        isBaseline: true,
        isCurrent: true,
      },
    ];
  }, [fallbackMode, revisions, project.startDate, project.endDatePlanned, project.commissioningDate]);

  const model = useMemo(
    () => buildRevisionLanes(effective, project, expanded ? Number.POSITIVE_INFINITY : 5),
    [effective, project, expanded],
  );

  if (!model || model.lanes.length === 0) {
    return (
      <PmoCard className="mb-4">
        <PmoCardHeader title="Programme timeline" />
        <p className="px-4 py-6 text-center text-[12px] text-[#94a3b8]">
          Set start and planned end dates (or import a schedule) to render the programme timeline.
        </p>
      </PmoCard>
    );
  }

  const { axisStart, axisEnd, lanes } = model;
  const showDeltas = !fallbackMode && lanes.length > 1;
  const todayRaw = pctOnAxis(now, axisStart, axisEnd);
  const todayPct = todayRaw > 0 && todayRaw < 100 ? todayRaw : null;
  const cod = project.commissioningDate ? new Date(project.commissioningDate) : null;
  const codPct = cod && !Number.isNaN(cod.getTime()) ? pctOnAxis(cod, axisStart, axisEnd) : null;

  const current = effective.find((r) => r.isCurrent) ?? effective[effective.length - 1];
  const planStart = current?.planStart ? new Date(current.planStart) : null;
  const planFinish = current?.planFinish ? new Date(current.planFinish) : null;
  const totalDays = planStart && planFinish ? Math.max(1, differenceInCalendarDays(planFinish, planStart)) : null;
  const elapsedDays = planStart ? Math.max(0, differenceInCalendarDays(now, planStart)) : null;
  const variance = actualPct == null ? null : actualPct - plannedTodayPct;

  const legend = (
    <div className="hidden items-center gap-3.5 text-[10.5px] text-slate-500 md:flex">
      <span className="flex items-center gap-1.5"><i className="h-1.5 w-4 rounded-[3px] bg-[#cfe3d8]" />Planned span</span>
      <span className="flex items-center gap-1.5"><i className="h-1.5 w-4 rounded-[3px] bg-[#006346]" />Actual progress</span>
      <span className="flex items-center gap-1.5"><i className="h-2.5 w-0.5 rounded-[1px] bg-[#b45309]" />Today</span>
      <span className="flex items-center gap-1.5"><i className="h-2 w-2 rotate-45 rounded-[1px] bg-[#475569]" />Milestone</span>
    </div>
  );

  return (
    <PmoCard className="mb-4">
      <PmoCardHeader
        title={lanes.length > 1 ? 'Programme timeline · revision comparison' : 'Programme timeline'}
        action={legend}
      />
      {revisionsUnavailable && (
        <p className="px-4 pt-2 text-[10.5px] text-[#94a3b8]">Revision history unavailable — showing the project plan window.</p>
      )}
      <div className="px-4 pb-3 pt-6">
        <div className="grid grid-cols-[168px_1fr]">
          <div />
          <div className="flex justify-between border-b border-[#f1f5f9] pb-0.5 text-[9.5px] font-bold uppercase tracking-[0.08em] text-[#94a3b8]">
            {model.ticks.map((t) => (
              <span key={`${t.label}-${t.pct}`}>{t.label}</span>
            ))}
          </div>
        </div>
        {model.hiddenEarlierCount > 0 && (
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded(true)}
            className="mt-2 text-[11px] font-semibold text-[#006346] hover:underline"
          >
            +{model.hiddenEarlierCount} earlier revisions
          </button>
        )}
        <div className="mt-3.5 grid grid-cols-[168px_1fr]">
          <div>
            {lanes.map((lane) => (
              <div key={lane.version} className="flex h-[44px] flex-col justify-center pr-3.5">
                <div className="text-[12px] font-bold text-slate-900">
                  {fallbackMode ? 'Plan' : lane.label}
                  {!fallbackMode && lane.isBaseline && (
                    <span className="ml-1.5 rounded-[5px] bg-[#E2EFDB] px-1.5 py-px text-[9px] font-bold uppercase tracking-[0.06em] text-[#076333]">
                      Baseline
                    </span>
                  )}
                  {!fallbackMode && lane.isCurrent && !lane.isBaseline && (
                    <span className="ml-1.5 rounded-[5px] bg-[#fffbeb] px-1.5 py-px text-[9px] font-bold uppercase tracking-[0.06em] text-[#b45309]">
                      Current
                    </span>
                  )}
                </div>
                <div className="mt-px text-[10.5px] text-[#94a3b8]">
                  {fallbackMode ? 'From project dates' : lane.metaLine}
                </div>
              </div>
            ))}
          </div>
          <div className="relative">
            {todayPct != null && (
              <div className="absolute inset-y-[-6px] z-10 w-[2px] bg-[#b45309]" style={{ left: `${todayPct}%` }}>
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-[5px] border border-[#fde68a] bg-[#fffbeb] px-1.5 text-[8.5px] font-bold tracking-[0.04em] text-[#b45309]">
                  TODAY · {format(now, 'dd MMM yy').toUpperCase()}
                </span>
              </div>
            )}
            {lanes.map((lane) => (
              <div key={lane.version} className="relative h-[44px]">
                <div className="absolute left-0 right-0 top-[16px] h-[12px] rounded-full bg-[#eef2f6]" />
                <div
                  className={`absolute top-[16px] h-[12px] rounded-full border border-[#aecfbe] ${
                    lane.isCurrent ? 'bg-[#e7f0eb]' : 'bg-[#cfe3d8]'
                  }`}
                  style={{ left: `${lane.leftPct}%`, width: `${lane.widthPct}%` }}
                />
                {lane.isCurrent && actualPct != null && actualPct > 0 && (
                  <div
                    className="absolute top-[16px] h-[12px] rounded-l-full bg-[#006346]"
                    style={{ left: `${lane.leftPct}%`, width: `${(lane.widthPct * actualPct) / 100}%` }}
                  />
                )}
                {lane.isCurrent &&
                  milestones.map((ms) => {
                    const d = new Date(ms.finish);
                    if (Number.isNaN(d.getTime())) return null;
                    return (
                      <span
                        key={`${ms.name}-${ms.finish}`}
                        title={`${ms.name} · ${fmtDay(d)}`}
                        tabIndex={0}
                        role="img"
                        aria-label={`${ms.name} · ${fmtDay(d)}`}
                        className="absolute top-[18px] h-2 w-2 -translate-x-1/2 rotate-45 rounded-[1px] bg-[#475569]"
                        style={{ left: `${pctOnAxis(d, axisStart, axisEnd)}%` }}
                      />
                    );
                  })}
                <span
                  className="absolute top-[13px] h-[18px] w-[2px] rounded-[1px] bg-[#64748b]"
                  style={{ left: `${Math.min(lane.leftPct + lane.widthPct, 99.5)}%` }}
                />
                {(showDeltas || lane.isBaseline) && !fallbackMode && (
                  <span
                    className={`absolute top-[14px] whitespace-nowrap rounded-full border px-1.5 py-px text-[9.5px] font-bold ${DELTA_CHIP[lane.deltaTone]}`}
                    style={{ left: `calc(${Math.min(lane.leftPct + lane.widthPct, 86)}% + 8px)` }}
                  >
                    {lane.deltaLabel}
                  </span>
                )}
                {lane.isCurrent && cod && codPct != null && (
                  <span className="absolute top-[2px] whitespace-nowrap text-[9px] font-bold text-[#b45309]" style={{ left: `${Math.min(codPct, 88)}%` }}>
                    COD ◆ {format(cod, 'dd MMM yy')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 px-0.5 pt-3 text-[10.5px] text-slate-500">
          <span>
            Start <b className="font-bold text-slate-900">{fmtDay(planStart)}</b>
            {elapsedDays != null && totalDays != null && (
              <>
                {' '}· elapsed <b className="font-bold text-slate-900">{elapsedDays} / {totalDays} days</b>
              </>
            )}
          </span>
          <span>
            Planned today <b className="font-bold text-slate-900">{plannedTodayPct}%</b> · actual{' '}
            <b className="font-bold text-slate-900">{actualPct ?? '—'}%</b> · variance{' '}
            <b className={`font-bold ${variance != null && variance < 0 ? 'text-[#be123c]' : 'text-slate-900'}`}>
              {variance == null ? '—' : `${variance >= 0 ? '+' : '−'}${Math.abs(variance)}%`}
            </b>
          </span>
          <span>
            Plan finish <b className="font-bold text-slate-900">{fmtDay(planFinish)}</b> · commissioning{' '}
            <b className="font-bold text-slate-900">{fmtDay(cod)}</b>
          </span>
        </div>
      </div>
    </PmoCard>
  );
}
